import { useState, useEffect, useRef, useMemo } from 'react';
import { CreditCard, AlertCircle, Loader2 } from 'lucide-react';
import { useAnalytics } from '@/lib/analytics';
import { useI18n } from '@/lib/i18n';
import { safeJsonParse } from './safe-json';
import { alipayYen, showsAlipayUpFront, type PaymentConfig } from './alipay-option';
import { AlipayMark, CardMarks } from './PaymentMarks';
import { readWalletDevice, walletMarks } from './wallet-marks';

type PaymentMethod = 'card' | 'alipay';

/**
 * The top-up half of the account card.
 *
 * A PANEL, not a card — see `BalancePanel` for why the two share one surface,
 * and why they nonetheless stay separate components: this half must keep
 * rendering its form when the balance fetch fails, and vice versa.
 */
export function AddFundsPanel({ onError }: { onError: (message: string) => void }) {
  const { trackEvent } = useAnalytics();
  const { t, locale } = useI18n();

  // State
  const [paymentConfig, setPaymentConfig] = useState<PaymentConfig | null>(null);
  // "Not fetched yet" is a different thing from "fetched, and Stripe is not
  // configured". Only the second may render "Payment unavailable".
  const [configLoading, setConfigLoading] = useState(true);

  // Top-up form state
  const [topUpAmount, setTopUpAmount] = useState<number>(10);
  const [isProcessing, setIsProcessing] = useState(false);
  // The picked method. Card until a China signal or "Other payment methods"
  // picks Alipay; the user's own pick is never overridden after that.
  const [method, setMethod] = useState<PaymentMethod>('card');
  const alipayDefaultAppliedRef = useRef(false);
  // "Other payment methods" pressed: the picker shown although no signal
  // pointed to China.
  const [alipayExpanded, setAlipayExpanded] = useState(false);
  // The wallets this device supports under the card option; read once.
  const [wallets] = useState(() => walletMarks(readWalletDevice()));

  // Ref to prevent duplicate fetches
  const fetchAttemptedRef = useRef(false);

  // Preset amounts
  const presetAmounts = [5, 10, 20, 50, 100];

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat(locale === 'zh' ? 'zh-CN' : locale === 'ja' ? 'ja-JP' : 'en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // Yen in the viewer's own locale, so a Chinese UI reads JP¥1,582 and never
  // mistakes it for yuan (ja: ￥1,582, en: ¥1,582).
  const formatYen = (amount: number): string =>
    new Intl.NumberFormat(locale === 'zh' ? 'zh-CN' : locale, { style: 'currency', currency: 'JPY' }).format(amount);

  const alipayUpFront = useMemo(
    () =>
      paymentConfig !== null &&
      showsAlipayUpFront({
        uiLocale: locale,
        browserLanguages: navigator.languages ?? [navigator.language],
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        chinaRegionIp: paymentConfig.chinaRegionIp,
      }),
    [paymentConfig, locale]
  );

  // Fetch payment config
  const fetchPaymentConfig = async () => {
    try {
      const response = await fetch('/api/payment/config', {
        credentials: 'include',
      });
      if (!response.ok) {
        // Stripe not configured or error
        setPaymentConfig(null);
        return;
      }
      const data = await safeJsonParse<PaymentConfig>(response);
      setPaymentConfig(data);
    } catch (err) {
      console.error('Error fetching payment config:', err);
      setPaymentConfig(null);
    } finally {
      setConfigLoading(false);
    }
  };

  // Default to Alipay once, when the config first shows a China signal.
  useEffect(() => {
    if (alipayUpFront && paymentConfig?.alipay && !alipayDefaultAppliedRef.current) {
      alipayDefaultAppliedRef.current = true;
      setMethod('alipay');
    }
  }, [alipayUpFront, paymentConfig]);

  // Alipay can vanish on a config refetch (no recent rate): fall back to card.
  const effectiveMethod: PaymentMethod = paymentConfig?.alipay ? method : 'card';
  const showMethodPicker = paymentConfig?.alipay != null && (alipayUpFront || alipayExpanded);

  // Handle top-up submission
  const handleTopUp = async (method: PaymentMethod) => {
    if (!paymentConfig) return;

    const amountCents = Math.round(topUpAmount * 100);

    if (amountCents < paymentConfig.minAmount) {
      onError(t('dashboard.wallet.minAmountError').replace('{min}', formatCurrency(paymentConfig.minAmount / 100)));
      return;
    }

    if (amountCents > paymentConfig.maxAmount) {
      onError(t('dashboard.wallet.maxAmountError').replace('{max}', formatCurrency(paymentConfig.maxAmount / 100)));
      return;
    }

    setIsProcessing(true);
    onError('');

    try {
      trackEvent('payment_initiated', { amount: topUpAmount, method });

      const response = await fetch('/api/payment/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        // For Alipay, the date of the rate the yen amount on this page came
        // from: the server charges only that amount.
        body: JSON.stringify({
          amount: amountCents,
          method,
          ...(method === 'alipay' ? { rateDate: paymentConfig.alipay?.rateDate } : {}),
        }),
      });

      const data = await safeJsonParse<{ url?: string; error?: string; code?: string }>(response);

      // The page sat open across the daily rate refresh: show the new yen
      // amount and let the user press again, rather than pay one they never saw.
      if (response.status === 409 && data?.code === 'rate_changed') {
        await fetchPaymentConfig();
        onError(t('dashboard.wallet.alipayRateChanged'));
        return;
      }

      if (!response.ok || !data) {
        throw new Error(data?.error || 'Failed to create checkout session');
      }

      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Error creating checkout:', err);
      onError(err instanceof Error ? err.message : t('dashboard.wallet.paymentError'));
      trackEvent('payment_error', { error: err instanceof Error ? err.message : 'Unknown error' });
    } finally {
      setIsProcessing(false);
    }
  };

  // Fetch this card's own config on mount (runs once, guarded against
  // React StrictMode's double-invoke)
  useEffect(() => {
    if (fetchAttemptedRef.current) return;
    fetchAttemptedRef.current = true;
    fetchPaymentConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="wallet-panel wallet-panel--add-funds">
      <span className="wallet-panel__eyebrow">{t('dashboard.wallet.topUp')}</span>
      {configLoading ? (
        <div className="wallet-card__loading">
          <Loader2 className="animate-spin" size={20} />
        </div>
      ) : paymentConfig ? (
        <>
          <div className="wallet-card__preset-amounts">
            {presetAmounts.map((amount) => (
              <button
                key={amount}
                className={`wallet-card__preset-btn ${topUpAmount === amount ? 'wallet-card__preset-btn--active' : ''}`}
                onClick={() => setTopUpAmount(amount)}
                disabled={isProcessing}
              >
                ${amount}
              </button>
            ))}
          </div>

          <div className="wallet-card__custom-amount">
            <label htmlFor="custom-amount">{t('dashboard.wallet.customAmount')}</label>
            <div className="wallet-card__amount-input">
              <span className="wallet-card__currency">$</span>
              <input
                id="custom-amount"
                type="number"
                min={paymentConfig.minAmount / 100}
                max={paymentConfig.maxAmount / 100}
                step="1"
                value={topUpAmount}
                onChange={(e) => setTopUpAmount(Math.max(0, parseInt(e.target.value) || 0))}
                disabled={isProcessing}
              />
            </div>
            <span className="wallet-card__amount-range">
              {t('dashboard.wallet.amountRange')
                .replace('{min}', formatCurrency(paymentConfig.minAmount / 100))
                .replace('{max}', formatCurrency(paymentConfig.maxAmount / 100))}
            </span>
          </div>

          {showMethodPicker && paymentConfig.alipay && (
            <div className="wallet-card__methods" role="radiogroup" aria-labelledby="payment-method-label">
              <span id="payment-method-label" className="wallet-card__methods-label">
                {t('dashboard.wallet.paymentMethod')}
              </span>
              <div className="wallet-card__method-tiles">
                <button
                  type="button"
                  role="radio"
                  aria-checked={effectiveMethod === 'card'}
                  className="wallet-card__method-tile"
                  onClick={() => setMethod('card')}
                  disabled={isProcessing}
                >
                  <span className="wallet-card__method-name">
                    <CreditCard size={20} />
                    {t('dashboard.wallet.methodCard')}
                  </span>
                  <CardMarks wallets={wallets} />
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={effectiveMethod === 'alipay'}
                  className="wallet-card__method-tile"
                  onClick={() => setMethod('alipay')}
                  disabled={isProcessing}
                >
                  <span className="wallet-card__method-name">
                    <AlipayMark />
                    {t('dashboard.wallet.methodAlipay')}
                  </span>
                  <span className="wallet-card__method-sub">JPY</span>
                </button>
              </div>
            </div>
          )}

          <button
            className="wallet-card__submit-btn"
            onClick={() => handleTopUp(effectiveMethod)}
            disabled={isProcessing || topUpAmount <= 0}
          >
            {isProcessing ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                {t('dashboard.wallet.processing')}
              </>
            ) : (
              /* "Continue to", not "Pay with": clicking this takes payment
                 nowhere — it hands off to Stripe's hosted page, and the user
                 pays there. The amount is what that page will charge: dollars
                 for card (Apple Pay, Google Pay and Link surface under Stripe's
                 `card` method type too), yen for Alipay. */
              t('dashboard.wallet.continueWithAmount').replace(
                '{amount}',
                effectiveMethod === 'alipay' && paymentConfig.alipay
                  ? formatYen(alipayYen(topUpAmount, paymentConfig.alipay))
                  : formatCurrency(topUpAmount)
              )
            )}
          </button>

          {effectiveMethod === 'alipay' && paymentConfig.alipay && (
            <div className="wallet-card__alipay-notes">
              {/* Charged in yen, not yuan: the account settles in JPY, so a
                  yen charge skips Stripe's conversion fee. The Alipay app
                  shows the payer yuan at its own rate before they confirm. */}
              {topUpAmount > 0 && (
                <p className="wallet-card__alipay-note">
                  {t('dashboard.wallet.alipayCharge').replace(
                    '{amount}',
                    formatYen(alipayYen(topUpAmount, paymentConfig.alipay))
                  )}
                </p>
              )}
              <p className="wallet-card__alipay-note">
                {(() => {
                  // The ISO date must not break at its hyphens.
                  const [before, after] = t('dashboard.wallet.alipayRate')
                    .replace('{rate}', paymentConfig.alipay.jpyPerUsd.toFixed(2))
                    .split('{date}');
                  return (
                    <>
                      {before}
                      <span className="wallet-card__nowrap">{paymentConfig.alipay.rateDate}</span>
                      {after}
                    </>
                  );
                })()}
              </p>
            </div>
          )}

          {paymentConfig.alipay && !showMethodPicker && (
            <button
              className="wallet-card__other-methods"
              onClick={() => {
                // The only other method is Alipay, and asking for it is why
                // the user pressed this.
                setAlipayExpanded(true);
                setMethod('alipay');
              }}
              disabled={isProcessing}
            >
              {t('dashboard.wallet.otherPaymentMethods')}
            </button>
          )}

          {/* Naming the processor is the ordinary trust signal for a handoff
              to a third-party payment page, and it is accurate: no card
              details ever reach this application. */}
          <p className="wallet-card__stripe-note">{t('dashboard.wallet.poweredByStripe')}</p>

        </>
      ) : (
        <div className="wallet-card__unavailable">
        <AlertCircle size={24} />
        <p>{t('dashboard.wallet.paymentUnavailable')}</p>
        </div>
      )}
    </div>
  );
}
